import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';

/** Field wrapper with a small uppercase label. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

export interface Option<T extends string> {
  value: T;
  label: string;
}

/** Ring/label styling shared by both chip controls for a live AI voice-capture suggestion — not a new source of truth, just a highlight prompting the officer toward a chip they'd otherwise click manually. */
const SUGGESTED_CLASSES = 'border-amber-400/60 bg-amber-400/10 text-amber-500 ring-1 ring-amber-400/50';

/** Single-select chip group (radio semantics). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  allowClear = false,
  suggested,
}: {
  options: Option<T>[];
  value: T | undefined;
  onChange: (v: T | undefined) => void;
  allowClear?: boolean;
  /** A live AI voice-capture suggestion for this field (value -> source transcript quote) — highlights the option and shows the quote on hover. Never applied automatically; accepting it is just clicking the option. */
  suggested?: { value: T; quote: string };
}) {
  return (
    <div role="radiogroup" className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = o.value === value;
        const isSuggested = !active && suggested?.value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(allowClear && active ? undefined : o.value)}
            title={isSuggested ? `Voice suggestion: "${suggested!.quote}"` : undefined}
            className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
              active
                ? 'border-accent/60 bg-accent/15 text-accent'
                : isSuggested
                  ? SUGGESTED_CLASSES
                  : 'border-border bg-surface-2 text-muted hover:text-text'
            }`}
          >
            {isSuggested && <Sparkles size={10} aria-hidden />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Multi-select chip group. */
export function TagToggles<T extends string>({
  options,
  selected,
  onToggle,
  suggested,
}: {
  options: Option<T>[];
  selected: readonly T[];
  onToggle: (v: T) => void;
  /** Tags with a live AI voice-capture suggestion (tag -> source transcript quote) — highlights the not-yet-selected chip and shows the quote on hover. Never applied automatically; accepting one is just clicking the chip. */
  suggested?: Partial<Record<T, string>>;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = selected.includes(o.value);
        const quote = suggested?.[o.value];
        const isSuggested = !active && quote !== undefined;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(o.value)}
            title={isSuggested ? `Voice suggestion: "${quote}"` : undefined}
            className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
              active
                ? 'border-accent/60 bg-accent/15 text-accent'
                : isSuggested
                  ? SUGGESTED_CLASSES
                  : 'border-border bg-surface-2 text-muted hover:text-text'
            }`}
          >
            {isSuggested && <Sparkles size={10} aria-hidden />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Compact single-line text input. */
export function TextInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className="w-full rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-sm text-text placeholder:text-muted focus:border-accent/60"
    />
  );
}
